import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getSkinByHostname } from '../skins/index.js'

const STYLE_TAG_ID = 'skin-overrides'

export const useSkinStore = defineStore('skin', () => {
  const skin = ref(null)

  /** Inizializza la skin in base all'hostname corrente. Chiamare una volta in App.vue. */
  function init() {
    const hostname = window.location.hostname
    skin.value = getSkinByHostname(hostname)
    _applyTheme(skin.value)
  }

  // --- Getters ---
  const skinId = computed(() => skin.value?.id ?? '')
  const appName = computed(() => skin.value?.appName ?? null)
  const sidebarTitle = computed(() => skin.value?.sidebarTitle ?? null)
  const description = computed(() => skin.value?.description ?? '')
  const features = computed(() => skin.value?.features ?? [])
  const logo = computed(() => skin.value?.logo ?? null)
  const icon = computed(() => skin.value?.icon ?? null)
  const footer = computed(() => skin.value?.footer ?? '')

  function _applyTheme(skinConfig) {
    if (!skinConfig) return
    _applyCssVariables(skinConfig.colors)
    _injectAeVueOverrides(skinConfig.colors)
  }

  /** Applica le CSS custom properties su :root */
  function _applyCssVariables(colors) {
    if (!colors) return
    const root = document.documentElement

    for (const [prop, value] of Object.entries(colors)) {
      root.style.setProperty(prop, value)
    }

    // Mappa --skin-primary → --primary-color (usata dai componenti poderi2)
    const primary = colors['--skin-primary']
    if (primary) {
      root.style.setProperty('--primary-color', primary)
    }
  }

  /**
   * Inietta un <style> tag per sovrascrivere i colori hardcoded di AeVue (glass theme).
   * Questo è necessario perché AeVue non usa CSS variables per i colori primari.
   */
  function _injectAeVueOverrides(colors) {
    const primary = colors?.['--skin-primary']
    const primaryHover = colors?.['--skin-primary-hover']
    if (!primary) return

    // Rimuove eventuale style tag precedente
    const existing = document.getElementById(STYLE_TAG_ID)
    if (existing) existing.remove()

    const style = document.createElement('style')
    style.id = STYLE_TAG_ID
    style.textContent = `
      /* Skin overrides per AeVue glass theme */
      [data-theme="glass"] .ae-button--primary {
        background: ${primary} !important;
      }
      [data-theme="glass"] .ae-button--primary:hover:not(:disabled) {
        background: ${primaryHover || primary} !important;
        box-shadow: 0 8px 24px ${primary}66;
      }
      [data-theme="glass"] .btn-primary {
        background-color: ${primary} !important;
      }
      [data-theme="glass"] .badge-primary {
        background-color: ${primary} !important;
      }
      [data-theme="glass"] .progress-fill {
        background-color: ${primary} !important;
      }

      [data-theme="glass"] .ae-sidebar {
        background: ${primary}99 !important;
        color: #FFFFFF !important;
      } 
      [data-theme="glass"] .ae-navbar {
        background: ${primary}99 !important;
        color: #FFFFFF !important;
      }
    `;
    document.head.appendChild(style)
  }

  return {
    skin,
    skinId,
    appName,
    sidebarTitle,
    description,
    features,
    logo,
    icon,
    footer,
    init,
  }
})
